import { useRef, useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, Paperclip, Link2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DOC_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.csv,.txt,.rtf,.zip,.ics,.jpg,.jpeg,.png,.webp,.gif";
const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const size = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(b / 1024))} Ko`);

const SNIPPETS = {
  image: (u, f) => `\n<img src="${u}" alt="${esc(f.filename)}" width="560" style="display:block;max-width:100%;height:auto;border:0;border-radius:8px;margin:12px 0;" />\n`,
  button: (u, f) => `\n<a href="${u}" target="_blank" style="display:inline-block;background:#e33170;color:#ffffff;padding:10px 20px;border-radius:999px;font-family:Helvetica,Arial,sans-serif;font-size:14px;font-weight:bold;text-decoration:none;">&#128206; Télécharger ${esc(f.filename)} (${size(f.size)})</a>\n`,
  link: (u, f, label) => `<a href="${u}" target="_blank" style="color:#722F37;text-decoration:underline;">${esc(label || f.filename)}</a>`,
};

/** Upload buttons that insert an image / download button / inline link at the textarea cursor. */
export default function EmailAssetToolbar({ textareaRef, value, onChange, testidPrefix = "email-asset" }) {
  const inputRef = useRef(null);
  const sel = useRef({ start: null, end: null, mode: "image" });
  const [busy, setBusy] = useState(null);

  const pick = (mode) => {
    const ta = textareaRef.current;
    sel.current = { start: ta?.selectionStart ?? (value || "").length, end: ta?.selectionEnd ?? (value || "").length, mode };
    inputRef.current.accept = mode === "image" ? "image/jpeg,image/png,image/webp,image/gif" : DOC_ACCEPT;
    inputRef.current.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const { start, end, mode } = sel.current;
    setBusy(mode);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/email-assets?kind=${mode === "image" ? "image" : "document"}`, fd);
      const text = value || "";
      const selected = text.slice(start, end).trim();
      const snippet = SNIPPETS[mode](`${BACKEND}${data.url}`, data, selected);
      onChange(text.slice(0, start) + snippet + text.slice(mode === "link" ? end : start));
      toast.success(mode === "image" ? "Image insérée" : mode === "button" ? "Bouton de téléchargement inséré" : "Lien inséré");
      requestAnimationFrame(() => {
        const ta = textareaRef.current;
        if (ta) { ta.focus(); ta.selectionStart = ta.selectionEnd = start + snippet.length; }
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'upload");
    } finally {
      setBusy(null);
    }
  };

  const Btn = ({ mode, icon: Icon, label, title }) => (
    <button type="button" onClick={() => pick(mode)} disabled={!!busy} title={title}
      className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1 text-[11px] font-semibold text-navy transition-colors hover:border-wine-300 hover:bg-wine-50 disabled:opacity-50"
      data-testid={`${testidPrefix}-${mode}`}>
      {busy === mode ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />} {label}
    </button>
  );

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={`${testidPrefix}-toolbar`}>
      <Btn mode="image" icon={ImageIcon} label="Insérer une image" title="L'image s'affiche dans l'e-mail, à l'endroit du curseur" />
      <Btn mode="button" icon={Paperclip} label="Pièce jointe (bouton)" title="Ajoute un bouton « Télécharger » vers le fichier, à l'endroit du curseur" />
      <Btn mode="link" icon={Link2} label="Lien vers un fichier" title="Insère un lien texte vers le fichier (sélectionnez d'abord un texte pour l'utiliser comme libellé)" />
      <span className="text-[10px] text-neutral-400">Max 25 Mo · PDF, Word, Excel, PowerPoint, ZIP, images…</span>
      <input ref={inputRef} type="file" className="hidden" onChange={onFile} data-testid={`${testidPrefix}-input`} />
    </div>
  );
}
